import {
  Badge,
  Box,
  Grid,
  Group,
  Stack,
  Table,
  Text,
  ThemeIcon,
  UnstyledButton,
} from "@mantine/core";
import { IconAlertTriangle, IconCheck } from "@tabler/icons-react";
import TopTreks from "../../../sales/components/Charts/TopTreks";
import FollowupDoneButton from "../../../sales/components/FollowupDoneButton/FollowupDoneButton";
import adminClasses from "../AdminDashboardView/AdminDashboardView.module.css";
import salesClasses from "./SalesDashboardView.module.css";
import {
  followupLeadNo,
  followupPerson,
  formatFollowupDue,
  leadName,
  leadStatusBadge,
  personInitials,
  priorityBadge,
} from "./salesDashboard.utils";

const AVATAR_COLORS = ["#f97316", "#8b5cf6", "#22c55e", "#3b82f6", "#ec4899", "#eab308"];

const recentLeadSubtitle = (lead) => {
  const trek = lead?.trek || lead?.plan_name || lead?.activity || "—";
  const leadNo = lead?.lead_no || (lead?.id ? `LID-${String(lead.id).padStart(4, "0")}` : "—");
  return `${trek} · ${leadNo}`;
};

const RecentLeadStatusBadge = ({ status }) => {
  const { label } = leadStatusBadge(status);
  const key = String(status || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  const isWon = key === "CLOSED_WON";
  return (
    <span
      className={
        isWon ? salesClasses.recentLeadStatusBadgeWon : salesClasses.recentLeadStatusBadge
      }
    >
      <span
        className={isWon ? salesClasses.recentLeadStatusDotWon : salesClasses.recentLeadStatusDot}
      />
      {label}
    </span>
  );
};

const RecentLeadsList = ({ leads }) => (
  <div className={salesClasses.recentLeadList}>
    {leads.map((lead, index) => {
      const name = leadName(lead);
      return (
        <div key={lead.id ?? lead.lead_no ?? index} className={salesClasses.recentLeadRow}>
          <div
            className={salesClasses.recentLeadAvatar}
            style={{ background: AVATAR_COLORS[index % AVATAR_COLORS.length] }}
          >
            {personInitials(name)}
          </div>
          <div className={salesClasses.recentLeadBody}>
            <Text className={salesClasses.recentLeadName} truncate>
              {name}
            </Text>
            <Text className={salesClasses.recentLeadMeta} truncate>
              {recentLeadSubtitle(lead)}
            </Text>
          </div>
          <div className={salesClasses.recentLeadTags}>
            <RecentLeadStatusBadge status={lead?.status} />
            <span className={salesClasses.recentLeadTypeBadge}>{lead?.lead_type || "B2C"}</span>
          </div>
        </div>
      );
    })}
  </div>
);

const PanelShell = ({
  title,
  titleClassName = "",
  linkClassName = "",
  linkLabel,
  onLinkClick,
  loading,
  emptyText,
  children,
  className = "",
}) => (
  <div className={`${adminClasses.card} ${salesClasses.equalPanel} ${className}`}>
    <Group justify="space-between" mb={10} wrap="nowrap">
      <Text className={`${adminClasses.cardHeader} ${titleClassName}`} mb={0}>
        {title}
      </Text>
      {linkLabel ? (
        <UnstyledButton onClick={onLinkClick} className={`${salesClasses.panelLink} ${linkClassName}`}>
          {linkLabel}
        </UnstyledButton>
      ) : null}
    </Group>
    {loading ? (
      <Text size="sm" c="dimmed">
        Loading...
      </Text>
    ) : emptyText ? (
      <Text size="sm" c="dimmed">
        {emptyText}
      </Text>
    ) : (
      children
    )}
  </div>
);

const SalesDashboardActivityPanels = ({ dashboard, onNavigate }) => {
  const {
    recentLeads,
    isRecentLeadsLoading,
    topTreks,
    isStatsLoading,
    todayFollowups,
    isTodayFollowupsLoading,
    overdueFollowups,
    overdueFollowupsCount,
    isOverdueFollowupsLoading,
    revalidateFollowups,
  } = dashboard;

  return (
    <>
      <Grid gutter={12} align="stretch">
        <Grid.Col span={{ base: 12, md: 4 }}>
          <PanelShell
            title="Recent Leads"
            titleClassName={salesClasses.recentLeadsTitle}
            linkClassName={salesClasses.recentLeadsLink}
            linkLabel="View all →"
            onLinkClick={onNavigate}
            loading={isRecentLeadsLoading}
            emptyText={recentLeads.length ? undefined : "No recent leads"}
          >
            <RecentLeadsList leads={recentLeads} />
          </PanelShell>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <PanelShell
            title="Top Activities"
            loading={isStatsLoading}
            emptyText={topTreks.length ? undefined : "No activity data"}
          >
            <TopTreks topTreks={topTreks} showToolbar={false} />
          </PanelShell>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <PanelShell
            title="Today's Follow-ups"
            linkLabel="View all →"
            onLinkClick={onNavigate}
            loading={isTodayFollowupsLoading}
          >
            {todayFollowups.length === 0 ? (
              <Stack align="center" gap="xs" py="lg">
                <ThemeIcon size={52} radius="md" className={salesClasses.clearTodayIcon}>
                  <IconCheck size={26} stroke={2.2} />
                </ThemeIcon>
                <Text size="sm" c="dimmed" fw={500}>
                  All clear today
                </Text>
              </Stack>
            ) : (
              <Stack gap={8}>
                {todayFollowups.slice(0, 5).map((item, index) => (
                  <Group key={item.id ?? index} justify="space-between" wrap="nowrap" gap="sm">
                    <Box style={{ minWidth: 0, flex: 1 }}>
                      <Text size="sm" fw={600} truncate>
                        {followupPerson(item)}
                      </Text>
                      <Text size="xs" c="dimmed" truncate>
                        {followupLeadNo(item)}
                      </Text>
                    </Box>
                    <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                      {formatFollowupDue(item)}
                    </Text>
                  </Group>
                ))}
              </Stack>
            )}
          </PanelShell>
        </Grid.Col>
      </Grid>

      <div className={`${adminClasses.card} ${salesClasses.overdueCard}`}>
        <div className={salesClasses.overdueHeader}>
          <Group gap={8} wrap="nowrap">
            <IconAlertTriangle size={16} color="#dc2626" stroke={2} />
            <Text className={salesClasses.overdueTitle}>Overdue Follow-ups</Text>
          </Group>
          <Badge color="red" variant="light" radius="sm" tt="none">
            {overdueFollowupsCount} total
          </Badge>
        </div>
        {isOverdueFollowupsLoading ? (
          <Text size="sm" c="dimmed" p="md">
            Loading...
          </Text>
        ) : overdueFollowups.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="lg">
            No overdue follow-ups
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={720}>
            <Table verticalSpacing="sm" highlightOnHover className={salesClasses.overdueTable}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Person</Table.Th>
                  <Table.Th>Lead</Table.Th>
                  <Table.Th>Phone</Table.Th>
                  <Table.Th>Due date</Table.Th>
                  <Table.Th>Priority</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {overdueFollowups.map((item, index) => {
                  const priority = priorityBadge(item.priority);
                  return (
                    <Table.Tr key={item.id ?? index}>
                      <Table.Td>
                        <Text size="sm" fw={600}>
                          {followupPerson(item)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={600} c="#ea580c">
                          {followupLeadNo(item)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {item.phone || "—"}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={500} c="#dc2626">
                          {formatFollowupDue(item)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="light" color={priority.color} size="sm" radius="sm" tt="none">
                          {priority.label}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <FollowupDoneButton
                          followup={item}
                          followupId={item.id}
                          status={item.status}
                          onCompleted={revalidateFollowups}
                        />
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </div>
    </>
  );
};

export default SalesDashboardActivityPanels;
